import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { AdminQuestionnaireTemplatesTable } from '../-components/AdminQuestionnaireTemplatesTable';
import { CreateTemplateDialog } from '../-components/CreateTemplateDialog';
import { DeleteTemplatesDialog } from '../-components/DeleteTemplatesDialog';
import { adminQuestionnaireTemplatesQueryOptions } from '../-hooks/useAdminQuestionnaireTemplates';
import { useAdminQuestionnaireTemplatesPage } from '../-hooks/useAdminQuestionnaireTemplatesPage';
import { MAX_COUNT } from '../-utils/listParams';

const adminTemplatesSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  /** The footer offers 50 / 100 / 500 and the route caps `count` at 500. */
  limit: fallback(
    z
      .number()
      .int()
      .transform(v => Math.min(Math.max(v, 10), MAX_COUNT)),
    50,
  ),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute(
  '/management/supplier-questionnaire-templates/(pages)/',
)({
  validateSearch: adminTemplatesSearch,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed on purpose: the loader is a prefetch, and letting it reject hands
  // the route to the router's error boundary, which replaces the page and hides
  // the table's own "could not load" banner. The component's `useQuery` hits
  // the same failure and surfaces it inline.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(adminQuestionnaireTemplatesQueryOptions(deps))
      .catch(() => undefined),
  component: AdminQuestionnaireTemplatesPage,
});

function AdminQuestionnaireTemplatesPage() {
  const page = useAdminQuestionnaireTemplatesPage();

  return (
    <PageContainer>
      <AdminQuestionnaireTemplatesTable page={page} />
      <CreateTemplateDialog
        open={page.isCreateOpen}
        isPending={page.isCreating}
        onClose={page.closeCreate}
        onSubmit={page.submitCreate}
      />
      <DeleteTemplatesDialog
        pending={page.pendingDelete}
        isPending={page.isDeleting}
        onCancel={page.cancelDelete}
        onConfirm={() => void page.confirmDelete()}
      />
    </PageContainer>
  );
}
